// Confirming a change of email address, from the link sent to the NEW address.
//
// Works signed in or signed out, like verify-email and reset-password: the
// person is reading mail on whichever device is nearest, and demanding a
// session would send them to a sign-in page for an account whose address is
// mid-change.
//
// Unlike the cancel-a-2FA-reset page, this one asks before acting. That page
// moves the account towards safety; this one changes what you sign in with, so
// a link followed by accident should not do it.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { AuthCard, Banner } from '../ui';

export function ConfirmEmailChangePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setState('busy');
    setError(null);
    try {
      const result = await api.confirmEmailChange(token);
      setEmail(result.email);
      setState('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setState('idle');
    }
  }

  if (state === 'done') {
    return (
      <AuthCard title="Address changed">
        <Banner kind="ok">You now sign in with {email}.</Banner>
        <p className="footnote">
          <Link to="/login">Sign in</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Confirm your new email address">
      {error && <Banner kind="error">{error}</Banner>}
      <p className="mb-4 text-sm text-gray-500">
        Confirming moves the Dayboard account to this address. Sign-in and password reset
        emails will come here from then on.
      </p>
      <button
        className="btn-primary full"
        type="button"
        disabled={state === 'busy' || token === ''}
        onClick={() => void submit()}
      >
        {state === 'busy' ? 'Confirming…' : 'Confirm'}
      </button>
      <p className="footnote">
        Not expecting this? Close this page; nothing changes, and the account stays on its
        current address.
      </p>
    </AuthCard>
  );
}
